import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { ChatConversation, ChatMessage } from '@limo/database'
import { formatDistanceToNow, format } from 'date-fns'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import {
  MessageSquare,
  Search,
  User,
  Bot,
  Clock,
  ArrowRight,
  CheckCircle,
  XCircle,
  UserCheck,
} from 'lucide-react'

type ConversationStatus = 'active' | 'resolved' | 'transferred' | 'abandoned'

type ConversationWithMessages = ChatConversation & {
  chat_messages: ChatMessage[]
}

const statusConfig: Record<
  ConversationStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle }
> = {
  active: { label: 'Active', variant: 'default', icon: Clock },
  resolved: { label: 'Resolved', variant: 'outline', icon: CheckCircle },
  transferred: { label: 'Transferred', variant: 'secondary', icon: UserCheck },
  abandoned: { label: 'Abandoned', variant: 'destructive', icon: XCircle },
}

export function ChatHistoryPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()

  const [statusFilter, setStatusFilter] = useState<ConversationStatus | 'all'>('all')
  const [search, setSearch] = useState('')
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch conversations
  const { data: conversations, isLoading } = useQuery({
    queryKey: ['chat-conversations', activeAccount?.id, statusFilter],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      let query = supabase
        .from('chat_conversations')
        .select(`
          *,
          chat_messages (*)
        `)
        .eq('account_id', activeAccount.id)
        .order('created_at', { ascending: false })

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      const { data, error } = await query
      if (error) throw error

      // Sort messages within each conversation
      return (data as ConversationWithMessages[]).map((conv) => ({
        ...conv,
        chat_messages: conv.chat_messages?.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        ) || [],
      }))
    },
    enabled: !!activeAccount?.id,
  })

  // Filter conversations by search
  const filteredConversations = conversations?.filter((conv) => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      conv.visitor_id.toLowerCase().includes(searchLower) ||
      conv.chat_messages?.some((msg) =>
        msg.content.toLowerCase().includes(searchLower)
      )
    )
  })

  const selectedConversation = conversations?.find((c) => c.id === selectedConversationId)

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a')
    } catch {
      return dateStr
    }
  }

  const formatRelativeDate = (dateStr: string) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true })
    } catch {
      return dateStr
    }
  }

  const getVisitorDisplayId = (visitorId: string) => {
    // Show first 8 chars of visitor ID for anonymization
    return `Visitor ${visitorId.slice(0, 8)}...`
  }

  const getMessagePreview = (messages: ChatMessage[]) => {
    const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user')
    if (!lastUserMessage) return 'No messages'
    const content = lastUserMessage.content
    return content.length > 60 ? content.slice(0, 60) + '...' : content
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Chat History</h1>
          <p className="text-muted-foreground">
            View past conversations with the AI assistant
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search messages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="resolved">Resolved</TabsTrigger>
            <TabsTrigger value="transferred">Transferred</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : !filteredConversations || filteredConversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No conversations</h3>
          <p className="text-muted-foreground max-w-md">
            {statusFilter === 'all'
              ? 'Chat conversations will appear here once visitors start chatting.'
              : `No ${statusFilter} conversations found.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[600px]">
          {/* Conversation list */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-muted/50 px-4 py-3 border-b">
              <h3 className="font-semibold">Conversations ({filteredConversations.length})</h3>
            </div>
            <ScrollArea className="h-[calc(600px-53px)]">
              <div className="divide-y">
                {filteredConversations.map((conv) => {
                  const StatusIcon = statusConfig[conv.status as ConversationStatus]?.icon || Clock
                  return (
                    <button
                      key={conv.id}
                      onClick={() => setSelectedConversationId(conv.id)}
                      className={cn(
                        'w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors',
                        selectedConversationId === conv.id && 'bg-muted'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">
                              {getVisitorDisplayId(conv.visitor_id)}
                            </span>
                            <Badge
                              variant={statusConfig[conv.status as ConversationStatus]?.variant || 'outline'}
                              className="text-xs"
                            >
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig[conv.status as ConversationStatus]?.label || conv.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground truncate mt-1">
                            {getMessagePreview(conv.chat_messages)}
                          </p>
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatRelativeDate(conv.created_at)}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>{conv.chat_messages.length} messages</span>
                      </div>
                    </button>
                  )
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Message thread */}
          <div className="border rounded-lg overflow-hidden">
            {selectedConversation ? (
              <>
                <div className="bg-muted/50 px-4 py-3 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">
                        {getVisitorDisplayId(selectedConversation.visitor_id)}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Started {formatDate(selectedConversation.created_at)}
                      </p>
                    </div>
                    <Badge
                      variant={statusConfig[selectedConversation.status as ConversationStatus]?.variant || 'outline'}
                    >
                      {statusConfig[selectedConversation.status as ConversationStatus]?.label || selectedConversation.status}
                    </Badge>
                  </div>
                </div>
                <ScrollArea className="h-[calc(600px-77px)]">
                  <div className="p-4 space-y-4">
                    {selectedConversation.chat_messages.map((message) => (
                      <div
                        key={message.id}
                        className={cn(
                          'flex gap-3',
                          message.role === 'user' && 'flex-row-reverse'
                        )}
                      >
                        <div
                          className={cn(
                            'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
                            message.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : message.role === 'agent'
                              ? 'bg-green-500 text-white'
                              : 'bg-muted'
                          )}
                        >
                          {message.role === 'user' ? (
                            <User className="h-4 w-4" />
                          ) : message.role === 'agent' ? (
                            <UserCheck className="h-4 w-4" />
                          ) : (
                            <Bot className="h-4 w-4" />
                          )}
                        </div>
                        <div
                          className={cn(
                            'max-w-[80%] rounded-lg px-3 py-2',
                            message.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : message.role === 'agent'
                              ? 'bg-green-100 dark:bg-green-900'
                              : 'bg-muted'
                          )}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          <p
                            className={cn(
                              'text-xs mt-1',
                              message.role === 'user'
                                ? 'text-primary-foreground/70'
                                : 'text-muted-foreground'
                            )}
                          >
                            {format(new Date(message.created_at), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <ArrowRight className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Select a conversation to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
